import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  IconButton,
  Tabs,
  Tab,
  Divider,
  InputLabel,
  FormControl,
  Select,
} from '@mui/material';
import {
  ExpandMore,
  CloudUpload,
  Preview,
  Save,
  Edit,
  Delete,
  Add,
  Rule,
  School,
  Search,
} from '@mui/icons-material';
import { PageHeader } from './common';
import { apiService } from '../services/api';

const emptyGuidelineForm = {
  name: '',
  aspect: '',
  guideline_text: '',
  is_active: true,
};

const emptyFewShotForm = {
  name: '',
  aspect: '',
  few_shot_example_text: '',
  is_active: true,
};

const GuidelinesManager = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Shared state
  const [toolId, setToolId] = useState(7);
  const [aspects, setAspects] = useState([]);
  const [aspectLoading, setAspectLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Guidelines upload state
  const [file, setFile] = useState(null);
  const [matchMode, setMatchMode] = useState('fuzzy');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Guidelines existing state
  const [existingGuidelines, setExistingGuidelines] = useState([]);
  const [guidelineModalOpen, setGuidelineModalOpen] = useState(false);
  const [editingGuideline, setEditingGuideline] = useState(null);
  const [guidelineForm, setGuidelineFormData] = useState(emptyGuidelineForm);

  // Few-shot examples state
  const [existingFewShots, setExistingFewShots] = useState([]);
  const [fewShotModalOpen, setFewShotModalOpen] = useState(false);
  const [editingFewShot, setEditingFewShot] = useState(null);
  const [fewShotForm, setFewShotFormData] = useState(emptyFewShotForm);

  // Search & filter state
  const [guidelineSearch, setGuidelineSearch] = useState('');
  const [guidelineAspectFilter, setGuidelineAspectFilter] = useState('all');
  const [guidelineStatusFilter, setGuidelineStatusFilter] = useState('all');
  const [fewShotSearch, setFewShotSearch] = useState('');
  const [fewShotAspectFilter, setFewShotAspectFilter] = useState('all');
  const [fewShotStatusFilter, setFewShotStatusFilter] = useState('all');

  // Load aspects
  const loadAspects = useCallback(async () => {
    setAspectLoading(true);
    try {
      const res = await apiService.aspects.list(toolId);
      setAspects(res.data || []);
    } catch (err) {
      console.error('Failed to load aspects:', err);
    } finally {
      setAspectLoading(false);
    }
  }, [toolId]);

  // Load existing guidelines
  const loadExistingGuidelines = useCallback(async () => {
    try {
      const res = await apiService.guidelines.list(toolId);
      setExistingGuidelines(res.data.guidelines || []);
    } catch (err) {
      console.error('Failed to load guidelines:', err);
      setMessage('Failed to load guidelines: ' + (err.response?.data?.detail || err.message));
    }
  }, [toolId]);

  // Load existing few-shot examples
  const loadExistingFewShots = useCallback(async () => {
    try {
      const res = await apiService.fewShotExamples.list(toolId);
      setExistingFewShots(res.data.few_shot_examples || []);
    } catch (err) {
      console.error('Failed to load few-shot examples:', err);
      setMessage('Failed to load few-shot examples: ' + (err.response?.data?.detail || err.message));
    }
  }, [toolId]);

  useEffect(() => {
    loadExistingGuidelines();
    loadExistingFewShots();
  }, [loadExistingGuidelines, loadExistingFewShots]);

  // Pre-load aspects when component mounts
  useEffect(() => {
    loadAspects();
  }, [loadAspects]);

  // --- Guidelines Upload & Preview ---
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreviewData(null);
    setMessage('');
  };

  const handlePreview = async () => {
    if (!file) {
      setMessage('Please select an Excel file first.');
      return;
    }
    setLoading(true);
    setMessage('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tool_id', toolId);
    formData.append('match_mode', matchMode);

    try {
      const res = await apiService.guidelines.preview(formData);
      setPreviewData(res.data);
      if (res.data.unmatched > 0) {
        setMessage(`Warning: ${res.data.unmatched} guidelines could not be matched to aspects.`);
      } else {
        setMessage(`All ${res.data.matched} guidelines matched successfully.`);
      }
    } catch (err) {
      setMessage('Preview failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (idx) => {
    const next = new Set(expandedRows);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpandedRows(next);
  };

  const handleGuidelineTextChange = (idx, newText) => {
    if (!previewData) return;
    const next = { ...previewData };
    next.guidelines = [...next.guidelines];
    next.guidelines[idx] = { ...next.guidelines[idx], guideline_text: newText };
    setPreviewData(next);
  };

  const handleToggleSelected = (idx) => {
    if (!previewData) return;
    const next = { ...previewData };
    next.guidelines = [...next.guidelines];
    next.guidelines[idx] = { ...next.guidelines[idx], selected: !next.guidelines[idx].selected };
    setPreviewData(next);
  };

  const handleSelectAll = () => {
    if (!previewData) return;
    const next = { ...previewData };
    next.guidelines = next.guidelines.map((g) => ({ ...g, selected: true }));
    setPreviewData(next);
  };

  const handleDeselectAll = () => {
    if (!previewData) return;
    const next = { ...previewData };
    next.guidelines = next.guidelines.map((g) => ({ ...g, selected: false }));
    setPreviewData(next);
  };

  const handleSave = async () => {
    if (!previewData) return;
    const selected = previewData.guidelines.filter((g) => g.selected);
    if (selected.length === 0) {
      setMessage('No guidelines selected to save.');
      return;
    }
    setSaveLoading(true);
    setMessage('');
    const payload = selected.map((g) => ({
      aspect_id: g.matched_aspect_id,
      name: `${g.task} - ${g.competence}`,
      guideline_text: g.guideline_text,
    }));

    try {
      const res = await apiService.guidelines.save({ guidelines: payload });
      setMessage(`Saved: ${res.data.created} created, ${res.data.updated} updated. Failed: ${res.data.failed}`);
      loadExistingGuidelines();
    } catch (err) {
      setMessage('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaveLoading(false);
    }
  };

  // --- Guidelines CRUD ---
  const handleOpenCreateGuideline = () => {
    setEditingGuideline(null);
    setGuidelineFormData(emptyGuidelineForm);
    setGuidelineModalOpen(true);
  };

  const handleOpenEditGuideline = (g) => {
    setEditingGuideline(g);
    setGuidelineFormData({
      name: g.name || '',
      aspect: g.aspect_id || g.aspect || '',
      guideline_text: g.guideline_text || '',
      is_active: g.is_active !== false,
    });
    setGuidelineModalOpen(true);
  };

  const handleCloseGuidelineModal = () => {
    setGuidelineModalOpen(false);
    setEditingGuideline(null);
  };

  const handleGuidelineFormChange = (field, value) => {
    setGuidelineFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveGuideline = async () => {
    if (!guidelineForm.name || !guidelineForm.aspect || !guidelineForm.guideline_text) {
      setMessage('Name, Aspect, and Guideline Text are required.');
      return;
    }
    setSaveLoading(true);
    setMessage('');
    const payload = {
      name: guidelineForm.name,
      aspect: Number(guidelineForm.aspect),
      guideline_text: guidelineForm.guideline_text,
      is_active: guidelineForm.is_active,
    };

    try {
      if (editingGuideline) {
        await apiService.guidelines.update(editingGuideline.id, payload);
        setMessage('Guideline updated successfully.');
      } else {
        await apiService.guidelines.create(payload);
        setMessage('Guideline created successfully.');
      }
      loadExistingGuidelines();
      handleCloseGuidelineModal();
    } catch (err) {
      setMessage('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteGuideline = async (id) => {
    if (!window.confirm('Are you sure you want to delete this guideline?')) return;
    try {
      await apiService.guidelines.delete(id);
      setMessage('Guideline deleted successfully.');
      loadExistingGuidelines();
    } catch (err) {
      setMessage('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // --- Few-Shot Examples CRUD ---
  const handleOpenCreateFewShot = () => {
    setEditingFewShot(null);
    setFewShotFormData(emptyFewShotForm);
    setFewShotModalOpen(true);
  };

  const handleOpenEditFewShot = (fs) => {
    setEditingFewShot(fs);
    setFewShotFormData({
      name: fs.name || '',
      aspect: fs.aspect_id || fs.aspect || '',
      few_shot_example_text: fs.few_shot_example_text || '',
      is_active: fs.is_active !== false,
    });
    setFewShotModalOpen(true);
  };

  const handleCloseFewShotModal = () => {
    setFewShotModalOpen(false);
    setEditingFewShot(null);
  };

  const handleFewShotFormChange = (field, value) => {
    setFewShotFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveFewShot = async () => {
    if (!fewShotForm.name || !fewShotForm.aspect || !fewShotForm.few_shot_example_text) {
      setMessage('Name, Aspect, and Example Text are required.');
      return;
    }
    setSaveLoading(true);
    setMessage('');
    const payload = {
      name: fewShotForm.name,
      aspect: Number(fewShotForm.aspect),
      few_shot_example_text: fewShotForm.few_shot_example_text,
      is_active: fewShotForm.is_active,
    };

    try {
      if (editingFewShot) {
        await apiService.fewShotExamples.update(editingFewShot.id, payload);
        setMessage('Few-shot example updated successfully.');
      } else {
        await apiService.fewShotExamples.create(payload);
        setMessage('Few-shot example created successfully.');
      }
      loadExistingFewShots();
      handleCloseFewShotModal();
    } catch (err) {
      setMessage('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteFewShot = async (id) => {
    if (!window.confirm('Are you sure you want to delete this few-shot example?')) return;
    try {
      await apiService.fewShotExamples.delete(id);
      setMessage('Few-shot example deleted successfully.');
      loadExistingFewShots();
    } catch (err) {
      setMessage('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const getMessageSeverity = () => {
    if (message.includes('Warning')) return 'warning';
    if (message.includes('failed') || message.includes('Error')) return 'error';
    return 'success';
  };

  // Computed filtered lists
  const filteredGuidelines = existingGuidelines.filter(g => {
    const q = guidelineSearch.toLowerCase();
    const matchSearch = !q || g.name?.toLowerCase().includes(q) || g.guideline_text?.toLowerCase().includes(q) || g.aspect_name?.toLowerCase().includes(q);
    const matchAspect = guidelineAspectFilter === 'all' || String(g.aspect) === String(guidelineAspectFilter);
    const matchStatus = guidelineStatusFilter === 'all' || (guidelineStatusFilter === 'active' ? g.is_active : !g.is_active);
    return matchSearch && matchAspect && matchStatus;
  });

  const filteredFewShots = existingFewShots.filter(fs => {
    const q = fewShotSearch.toLowerCase();
    const matchSearch = !q || fs.name?.toLowerCase().includes(q) || fs.few_shot_example_text?.toLowerCase().includes(q) || fs.aspect_name?.toLowerCase().includes(q);
    const matchAspect = fewShotAspectFilter === 'all' || String(fs.aspect) === String(fewShotAspectFilter);
    const matchStatus = fewShotStatusFilter === 'all' || (fewShotStatusFilter === 'active' ? fs.is_active : !fs.is_active);
    return matchSearch && matchAspect && matchStatus;
  });

  return (
    <Box>
      <PageHeader
        title="Guidelines & Examples Manager"
        subtitle="Manage evaluation guidelines and few-shot examples for aspects"
      />

      {/* Tool ID selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Tool ID"
                type="number"
                value={toolId}
                onChange={(e) => setToolId(Number(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Message */}
      {message && (
        <Alert severity={getMessageSeverity()} sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<Rule />} iconPosition="start" label={`Guidelines (${filteredGuidelines.length}${filteredGuidelines.length !== existingGuidelines.length ? ` / ${existingGuidelines.length}` : ''})`} />
          <Tab icon={<School />} iconPosition="start" label={`Few-Shot Examples (${filteredFewShots.length}${filteredFewShots.length !== existingFewShots.length ? ` / ${existingFewShots.length}` : ''})`} />
        </Tabs>

        {/* --- Guidelines Tab --- */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Upload IB Checklist Excel
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                  {file ? 'Change File' : 'Choose File'}
                  <input type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
                </Button>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  label="Match Mode"
                  value={matchMode}
                  onChange={(e) => setMatchMode(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="fuzzy">Fuzzy Match</MenuItem>
                  <MenuItem value="exact">Exact Match</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  onClick={handlePreview}
                  disabled={loading}
                  startIcon={<Preview />}
                  fullWidth
                >
                  {loading ? 'Parsing...' : 'Preview Guidelines'}
                </Button>
              </Grid>
            </Grid>
            {file && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </Typography>
            )}

            {/* Preview Results */}
            {previewData && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} component="span">
                      Preview Results
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                      Matched: {previewData.matched} / Unmatched: {previewData.unmatched}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={handleSelectAll}>Select All</Button>
                    <Button variant="outlined" size="small" onClick={handleDeselectAll}>Deselect All</Button>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleSave}
                      disabled={saveLoading}
                      startIcon={<Save />}
                    >
                      {saveLoading ? 'Saving...' : 'Save Selected'}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Task</TableCell>
                        <TableCell>Context</TableCell>
                        <TableCell>Managers</TableCell>
                        <TableCell>Competence</TableCell>
                        <TableCell>Matched Aspect</TableCell>
                        <TableCell>Rubrics</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.guidelines.map((g, idx) => (
                        <React.Fragment key={idx}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox checked={!!g.selected} onChange={() => handleToggleSelected(idx)} />
                            </TableCell>
                            <TableCell>{g.task}</TableCell>
                            <TableCell>{g.context}</TableCell>
                            <TableCell>{g.relevant_managers}</TableCell>
                            <TableCell>{g.competence}</TableCell>
                            <TableCell>
                              {g.matched_aspect_name ? (
                                <Typography color="success.main" fontWeight={700}>{g.matched_aspect_name}</Typography>
                              ) : (
                                <Typography color="error.main">Not matched</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {g.has_he && <Chip label="HE" color="success" size="small" />}
                                {g.has_eff && <Chip label="EFF" color="warning" size="small" />}
                                {g.has_ineff && <Chip label="INEFF" color="error" size="small" />}
                                {g.has_potential && <Chip label="POT" color="info" size="small" />}
                                {g.has_derailer && <Chip label="DER" color="secondary" size="small" />}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Button variant="outlined" size="small" onClick={() => toggleExpanded(idx)}>
                                {expandedRows.has(idx) ? 'Hide' : 'Edit'}
                              </Button>
                            </TableCell>
                          </TableRow>
                          {expandedRows.has(idx) && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ backgroundColor: 'action.hover', py: 2 }}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                  {g.task} - {g.competence}
                                </Typography>
                                <TextField
                                  multiline
                                  rows={4}
                                  value={g.guideline_text}
                                  onChange={(e) => handleGuidelineTextChange(idx, e.target.value)}
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {previewData.unmatched_guidelines?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="error.main" fontWeight={700} gutterBottom>
                      Unmatched Guidelines ({previewData.unmatched_guidelines.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell>Task</TableCell>
                            <TableCell>Competence</TableCell>
                            <TableCell>Rubrics</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.unmatched_guidelines.map((g, idx) => (
                            <TableRow key={`unmatched-${idx}`}>
                              <TableCell>{g.task}</TableCell>
                              <TableCell>{g.competence}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {g.has_he && <Chip label="HE" color="success" size="small" />}
                                  {g.has_eff && <Chip label="EFF" color="warning" size="small" />}
                                  {g.has_ineff && <Chip label="INEFF" color="error" size="small" />}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            )}

            {/* Search & Filter */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search guidelines..."
                  value={guidelineSearch}
                  onChange={(e) => setGuidelineSearch(e.target.value)}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aspect</InputLabel>
                  <Select value={guidelineAspectFilter} onChange={(e) => setGuidelineAspectFilter(e.target.value)} label="Aspect">
                    <MenuItem value="all">All Aspects</MenuItem>
                    {aspects.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={guidelineStatusFilter} onChange={(e) => setGuidelineStatusFilter(e.target.value)} label="Status">
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Existing Guidelines */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  Existing Guidelines ({filteredGuidelines.length}{filteredGuidelines.length !== existingGuidelines.length ? ` of ${existingGuidelines.length}` : ''})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenCreateGuideline}
                >
                  Create Guideline
                </Button>
              </Box>
              {filteredGuidelines.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  {existingGuidelines.length === 0 ? 'No guidelines found.' : 'No guidelines match your filters.'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {filteredGuidelines.map((g) => (
                    <Card key={g.id} variant="outlined" sx={{ borderLeft: 4, borderColor: g.is_active ? 'primary.main' : 'grey.400', opacity: g.is_active ? 1 : 0.6 }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {g.name}
                            </Typography>
                            <Chip label={g.aspect_name || 'Unknown'} size="small" color="primary" variant="outlined" />
                            {g.aspect_control_sum_id && (
                              <Chip label={`ID: ${g.aspect_control_sum_id}`} size="small" variant="outlined" />
                            )}
                            {!g.is_active && <Chip label="Inactive" size="small" color="default" />}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(g.created_at).toLocaleDateString()}
                            </Typography>
                            <IconButton size="small" onClick={() => handleOpenEditGuideline(g)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteGuideline(g.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                            {g.guideline_text}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* --- Few-Shot Examples Tab --- */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Few-Shot Examples ({filteredFewShots.length}{filteredFewShots.length !== existingFewShots.length ? ` of ${existingFewShots.length}` : ''})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reference evaluations that guide the LLM on how to score for each aspect.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreateFewShot}
              >
                Create Example
              </Button>
            </Box>

            {/* Search & Filter */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search examples..."
                  value={fewShotSearch}
                  onChange={(e) => setFewShotSearch(e.target.value)}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aspect</InputLabel>
                  <Select value={fewShotAspectFilter} onChange={(e) => setFewShotAspectFilter(e.target.value)} label="Aspect">
                    <MenuItem value="all">All Aspects</MenuItem>
                    {aspects.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={fewShotStatusFilter} onChange={(e) => setFewShotStatusFilter(e.target.value)} label="Status">
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {filteredFewShots.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {existingFewShots.length === 0 ? 'No few-shot examples found.' : 'No examples match your filters.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredFewShots.map((fs) => (
                  <Card key={fs.id} variant="outlined" sx={{ borderLeft: 4, borderColor: fs.is_active ? 'secondary.main' : 'grey.400', opacity: fs.is_active ? 1 : 0.6 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {fs.name}
                          </Typography>
                          <Chip label={fs.aspect_name || 'Unknown'} size="small" color="secondary" variant="outlined" />
                          {fs.aspect_control_sum_id && (
                            <Chip label={`ID: ${fs.aspect_control_sum_id}`} size="small" variant="outlined" />
                          )}
                          {!fs.is_active && <Chip label="Inactive" size="small" color="default" />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(fs.created_at).toLocaleDateString()}
                          </Typography>
                          <IconButton size="small" onClick={() => handleOpenEditFewShot(fs)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteFewShot(fs.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                          {fs.few_shot_example_text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Card>

      {/* ===== Guideline Create/Edit Dialog ===== */}
      <Dialog
        open={guidelineModalOpen}
        onClose={handleCloseGuidelineModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {editingGuideline ? 'Edit Guideline' : 'Create Guideline'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          {/* Meta section - fixed height */}
          <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={guidelineForm.name}
                  onChange={(e) => handleGuidelineFormChange('name', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Aspect</InputLabel>
                  <Select
                    label="Aspect"
                    value={guidelineForm.aspect}
                    onChange={(e) => handleGuidelineFormChange('aspect', e.target.value)}
                    disabled={aspectLoading}
                  >
                    {aspects.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name} (ID: {a.control_sum_id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={guidelineForm.is_active} onChange={(e) => handleGuidelineFormChange('is_active', e.target.checked)} />}
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
          {/* Text section - fills remaining space */}
          <Box sx={{ p: 3, pt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Guideline Text
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <TextField
                value={guidelineForm.guideline_text}
                onChange={(e) => handleGuidelineFormChange('guideline_text', e.target.value)}
                fullWidth
                multiline
                variant="outlined"
                required
                minRows={20}
                maxRows={20}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '100% !important',
                    alignItems: 'flex-start',
                    overflow: 'auto',
                  },
                  '& textarea': {
                    height: 'unset !important',
                    overflow: 'auto !important',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseGuidelineModal} variant="outlined">Cancel</Button>
          <Button onClick={handleSaveGuideline} variant="contained" disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Few-Shot Example Create/Edit Dialog ===== */}
      <Dialog
        open={fewShotModalOpen}
        onClose={handleCloseFewShotModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {editingFewShot ? 'Edit Few-Shot Example' : 'Create Few-Shot Example'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          {/* Meta section - fixed height */}
          <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Aspect</InputLabel>
                  <Select
                    label="Aspect"
                    value={fewShotForm.aspect}
                    onChange={(e) => handleFewShotFormChange('aspect', e.target.value)}
                    disabled={aspectLoading}
                  >
                    {aspects.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name} (ID: {a.control_sum_id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={fewShotForm.name}
                  onChange={(e) => handleFewShotFormChange('name', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={fewShotForm.is_active} onChange={(e) => handleFewShotFormChange('is_active', e.target.checked)} />}
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
          {/* Text section - fills remaining space */}
          <Box sx={{ p: 3, pt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Example Text
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <TextField
                value={fewShotForm.few_shot_example_text}
                onChange={(e) => handleFewShotFormChange('few_shot_example_text', e.target.value)}
                fullWidth
                multiline
                variant="outlined"
                required
                helperText="A complete few-shot example showing how a candidate evaluation should look for this aspect"
                minRows={20}
                maxRows={20}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '100% !important',
                    alignItems: 'flex-start',
                    overflow: 'auto',
                  },
                  '& textarea': {
                    height: 'unset !important',
                    overflow: 'auto !important',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseFewShotModal} variant="outlined">Cancel</Button>
          <Button onClick={handleSaveFewShot} variant="contained" disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuidelinesManager;